import { useEffect, useRef } from "react";
import { gql } from "graphql-request";
import { getClient } from "../utils/apiClient";

/**
 * Tell the backend which timezone this member is in, so anything we schedule
 * reaches them in their own morning rather than everyone's at one UTC hour.
 *
 * The device already knows the exact IANA zone and keeps it right through
 * daylight saving changes, so there is nothing to look up and nothing to
 * derive from a billing address. We only send it when it differs from what
 * the server already has, which means once at first login and again after a
 * move or a trip, not on every app open.
 *
 * Best effort by design: a failure here must never interrupt anyone, so it is
 * swallowed and simply retried the next time the app loads.
 */
const UPDATE_TIMEZONE = gql`
  mutation UpdateUserTimezone($timezone: String!) {
    updateUserTimezone(timezone: $timezone) {
      success
      message
    }
  }
`;

export function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function useReportTimezone(
  isLoggedIn: boolean,
  storedTimezone: string | null | undefined
): void {
  const inFlight = useRef(false);

  useEffect(() => {
    // `undefined` means the app row has not been merged in yet, which is not
    // the same as "the server has no zone for me"; waiting avoids a pointless
    // write on every cold start.
    if (!isLoggedIn || storedTimezone === undefined || inFlight.current) return;
    const tz = deviceTimeZone();
    if (!tz || tz === storedTimezone) return;

    inFlight.current = true;
    getClient()
      .request(UPDATE_TIMEZONE, { timezone: tz })
      .catch(() => {
        /* retried on the next load */
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [isLoggedIn, storedTimezone]);
}
